import React, { type ReactElement, type ReactNode } from "react";
import { Button, cn } from "@ndd/ui";
import type { NotificationListItem } from "../../../../shared/notification-list";
import { THEMED_CARD, THEMED_DIVIDE, THEMED_ROW_HOVER } from "../../lib/always-look";
import { appointmentIdOf, notificationTimeLabel } from "../../lib/notifications-client";

// The RD's in-app notification list (NO-830): one row per alert, newest first.
// Unread and read differ by weight and a neutral olive dot only, never a
// danger tone. A leaf module (`import React`, relative imports, no router, no
// queries) so `tsx --test` renders it; the page supplies the Appointment link
// through `renderAppointmentLink` and the read mark through `onOpen`.

export interface NotificationAppointmentLinkProps {
  appointmentId: string;
  className: string;
  onClick: () => void;
  dataAttributes: NotificationRowData;
  children: ReactNode;
}

export type RenderNotificationAppointmentLink = (
  props: NotificationAppointmentLinkProps
) => ReactElement;

interface NotificationRowData {
  "data-notification-id": string;
  "data-read": "true" | "false";
}

const ROW =
  "flex w-full items-start gap-3 px-5 py-3.5 text-left transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring";

function rowData(item: NotificationListItem): NotificationRowData {
  return {
    "data-notification-id": item.id,
    "data-read": item.readAt === null ? "false" : "true",
  };
}

function NotificationRowBody({
  item,
  timezone,
}: {
  item: NotificationListItem;
  timezone: string;
}): ReactElement {
  const unread = item.readAt === null;

  return (
    <>
      <span className="grid size-5 shrink-0 place-items-center pt-1">
        <span
          aria-hidden="true"
          className={cn(
            "size-2 rounded-full",
            unread ? "bg-primary" : "bg-transparent"
          )}
        />
      </span>
      <span className="flex min-w-0 flex-col gap-0.5">
        <span
          className={cn(
            "truncate text-body text-foreground",
            unread ? "font-semibold" : "font-normal text-muted-foreground"
          )}
        >
          {item.title}
        </span>
        <span className="truncate text-body-sm text-muted-foreground">
          {detailLine(item, timezone)}
        </span>
      </span>
      {unread ? <span className="sr-only">Unread</span> : null}
    </>
  );
}

function detailLine(item: NotificationListItem, timezone: string): string {
  const time = notificationTimeLabel(item, timezone);

  return time === null ? item.clientName : `${item.clientName} · ${time}`;
}

export function NotificationRow({
  item,
  timezone,
  onOpen,
  renderAppointmentLink,
}: {
  item: NotificationListItem;
  timezone: string;
  onOpen: (item: NotificationListItem) => void;
  renderAppointmentLink: RenderNotificationAppointmentLink;
}): ReactElement {
  const appointmentId = appointmentIdOf(item);
  const body = <NotificationRowBody item={item} timezone={timezone} />;
  const className = cn(ROW, THEMED_ROW_HOVER);

  if (!appointmentId) {
    return (
      <button
        type="button"
        className={className}
        onClick={() => onOpen(item)}
        {...rowData(item)}
      >
        {body}
      </button>
    );
  }

  return renderAppointmentLink({
    appointmentId,
    className,
    onClick: () => onOpen(item),
    dataAttributes: rowData(item),
    children: body,
  });
}

/** Loads the next older page; absent when the last page is loaded. */
export interface NotificationOlderPages {
  hasOlder: boolean;
  loadingOlder: boolean;
  onShowOlder: () => void;
}

function ShowOlderButton({
  hasOlder,
  loadingOlder,
  onShowOlder,
}: NotificationOlderPages): ReactElement | null {
  if (!hasOlder) return null;

  return (
    <div className="mt-4 flex justify-center">
      <Button
        type="button"
        variant="outline"
        disabled={loadingOlder}
        onClick={onShowOlder}
      >
        Show older
      </Button>
    </div>
  );
}

export function NotificationList({
  items,
  timezone,
  onOpen,
  renderAppointmentLink,
  older,
}: {
  items: readonly NotificationListItem[];
  timezone: string;
  onOpen: (item: NotificationListItem) => void;
  renderAppointmentLink: RenderNotificationAppointmentLink;
  older: NotificationOlderPages;
}): ReactElement {
  if (items.length === 0) {
    return <p className="mt-6 text-body text-muted-foreground">No notifications yet</p>;
  }

  return (
    <>
      <ul
        aria-label="Notifications"
        className={cn(
          "mt-6 divide-y divide-border overflow-hidden rounded-xl border border-border bg-card",
          THEMED_CARD,
          THEMED_DIVIDE
        )}
      >
        {items.map((item) => (
          <li key={item.id}>
            <NotificationRow
              item={item}
              timezone={timezone}
              onOpen={onOpen}
              renderAppointmentLink={renderAppointmentLink}
            />
          </li>
        ))}
      </ul>
      <ShowOlderButton {...older} />
    </>
  );
}

/** The page title, set in display type under the always theme. */
export function NotificationsHeading(): ReactElement {
  return (
    <h1 className="font-sans text-3xl font-bold tracking-tight text-foreground brand:text-display brand:leading-[1.15]">
      Notifications
    </h1>
  );
}
